import {Controller} from "@hotwired/stimulus"
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subMinutes,
  subDays,
  subHours,
  subYears
} from "date-fns"
import {fr, enGB, de} from "date-fns/locale"

const LOCALES = {
  fr: fr,
  en: enGB,
  de: de
}

export default class extends Controller {
  static targets = ["badge", "fromDateInput", "toDateInput"]

  static values = {
    translations: Object
  }

  applyRange({params: {key}}) {
    const [from, to] = this.rangeFor(key)

    this.fromDateInputTarget.value = from ? format(from, "yyyy-MM-dd'T'HH:mm") : ""
    this.toDateInputTarget.value = to ? format(to, "yyyy-MM-dd'T'HH:mm") : ""

    // trigger input events to notify any listeners of the change
    this.fromDateInputTarget.dispatchEvent(new Event("input", {bubbles: true}))
    this.toDateInputTarget.dispatchEvent(new Event("input", {bubbles: true}))
  }

  rangeFor(key) {
    const now = new Date()
    switch (key) {
      case "today":
        return [startOfDay(now), endOfDay(now)]
      case "yesterday":
        const yesterday = subDays(now, 1)
        return [startOfDay(yesterday), endOfDay(yesterday)]
      case "this_week":
        return [startOfWeek(now, {weekStartsOn: 1}), endOfWeek(now, {weekStartsOn: 1})]
      case "this_month":
        return [startOfMonth(now), endOfMonth(now)]
      case "this_year":
        return [startOfYear(now), endOfYear(now)]
      case "last_15_minutes":
        return [subMinutes(now, 15), now]
      case "last_30_minutes":
        return [subMinutes(now, 30), now]
      case "last_1_hour":
        return [subHours(now, 1), now]
      case "last_24_hours":
        return [subHours(now, 24), now]
      case "last_7_days":
        return [subDays(now, 7), now]
      case "last_30_days":
        return [subDays(now, 30), now]
      case "last_1_year":
        return [subYears(now, 1), now]
      default:
        throw new Error(`Unknown range key: ${key}`)
    }
  }

  updateBadge() {
    const fromDateValue = this.fromDateInputTarget.value
    const toDateValue = this.toDateInputTarget.value
    const badge = this.badgeTarget

    if (fromDateValue || toDateValue) {
      badge.classList.remove("hidden!")
      badge.innerHTML = this.badgeLabel(fromDateValue, toDateValue)
    } else {
      badge.classList.add("hidden!")
    }
  }

  badgeLabel(fromDate, toDate) {
    if (fromDate && toDate) {
      return this.translationsValue["between"]
          .replace("%{from}", format(new Date(fromDate), "Pp", {locale: this.currentLocale}))
          .replace("%{to}", format(new Date(toDate), "Pp", {locale: this.currentLocale}))
    } else if (fromDate) {
      return this.translationsValue["from"]
          .replace("%{date}", format(new Date(fromDate), "Pp", {locale: this.currentLocale}))
    } else if (toDate) {
      return this.translationsValue["to"]
          .replace("%{date}", format(new Date(toDate), "Pp", {locale: this.currentLocale}))
    }
  }

  get currentLocale() {
    return LOCALES[document.documentElement.lang] || fr
  }
}
